import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageHeader, SectionCard } from '@/components/admin/page';
import { api, ApiError } from '../../lib/api';
import {
  displayEmail,
  displayIdentifier,
  displayPersonName,
  displayReferralCode,
  displayTelegramHandle,
  displayWorkspaceName,
  formatDate,
  formatDateTime,
  formatNumber,
  planLabel,
} from '../../lib/format';
import { Pager } from '../../lib/Pager';
import { useI18n } from '../../lib/i18n';

interface TenantRow {
  id: string;
  slug: string;
  name: string;
  plan: string;
  entitlementOfflineGraceMinutes: number;
  createdAt: string;
  deletedAt: string | null;
  memberCount: number;
  employeeCount: number;
  conversationCount: number;
  ownerUserId: string | null;
  ownerEmail: string | null;
  ownerDisplayName: string | null;
  ownerStatus: string | null;
  ownerTelegramUsername: string | null;
  ownerTelegramChatId: string | null;
  signupApplicationId: string | null;
  signupStatus: string | null;
  requestedWorkspaceName: string | null;
  employeeNumber: string | null;
  nickname: string | null;
  tgName: string | null;
  referralCode: string | null;
  reviewedAt: string | null;
}

interface ListResponse {
  tenants: TenantRow[];
  total: number;
  limit: number;
  offset: number;
}

const PAGE_SIZE = 20;

const STATUS_KEY: Record<string, string> = {
  active: 'status.active',
  pending_review: 'status.pending_review',
  pending_telegram: 'status.pending_telegram',
  approved_pending_telegram: 'status.approved_pending_telegram',
  rejected: 'status.rejected',
  revoked: 'status.revoked',
  locked: 'status.locked',
};

export function TenantsPage() {
  const { t } = useI18n();
  const [showDeleted, setShowDeleted] = useState(false);
  const [rows, setRows] = useState<TenantRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState('');
  const [committedQ, setCommittedQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<TenantRow | null>(null);
  const [showAddWorkspace, setShowAddWorkspace] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', slug: '' });
  const [addLoading, setAddLoading] = useState(false);

  const buildParams = (overridePage?: number) => {
    const params = new URLSearchParams();
    if (committedQ) params.set('q', committedQ);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String((overridePage ?? page) * PAGE_SIZE));
    if (showDeleted) params.set('deleted', 'true');
    return params;
  };

  const reload = async (overridePage?: number) => {
    const r = await api<ListResponse>(`/v1/admin/tenants?${buildParams(overridePage)}`);
    setRows(r.tenants);
    setTotal(r.total);
    return r;
  };

  const search = () => { setPage(0); setCommittedQ(q.trim()); };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    reload()
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : (e as Error).message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, committedQ, showDeleted]);

  const updateOfflineGrace = async (tenant: TenantRow) => {
    const next = prompt(
      t('tenants.promptOffline'),
      String(tenant.entitlementOfflineGraceMinutes ?? 0),
    );
    if (next === null) return;
    const minutes = Number(next);
    if (!Number.isInteger(minutes) || minutes < 0 || minutes > 1440) {
      alert(t('tenants.alertOfflineRange'));
      return;
    }
    try {
      await api(`/v1/admin/tenants/${tenant.id}/policy`, {
        method: 'PATCH',
        body: JSON.stringify({ offlineGraceMinutes: minutes }),
      });
      const r = await reload();
      if (selected && selected.id === tenant.id) {
        const found = r.tenants.find((x) => x.id === tenant.id);
        if (found) setSelected(found);
      }
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const deleteTenant = async (tenant: TenantRow) => {
    if (!confirm(t('tenants.confirmDelete', { name: displayWorkspaceName(tenant.name) ?? tenant.name }))) return;
    try {
      await api(`/v1/admin/tenants/${tenant.id}`, { method: 'DELETE' });
      setSelected(null);
      await reload();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const restoreTenant = async (tenant: TenantRow) => {
    if (!confirm(t('tenants.confirmRestore', { name: displayWorkspaceName(tenant.name) ?? tenant.name }))) return;
    try {
      await api(`/v1/admin/tenants/${tenant.id}/restore-deleted`, { method: 'POST' });
      setSelected(null);
      await reload();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const addWorkspace = async () => {
    const name = addForm.name.trim();
    if (!name) { alert(t('tenants.addWorkspace.name')); return; }
    setAddLoading(true);
    try {
      await api('/v1/admin/tenants', {
        method: 'POST',
        body: JSON.stringify({
          name,
          slug: addForm.slug.trim() || undefined,
        }),
      });
      alert(t('tenants.addWorkspace.success', { name }));
      setAddForm({ name: '', slug: '' });
      setShowAddWorkspace(false);
      await reload();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setAddLoading(false);
    }
  };

  const statusLabel = (status: string | null): string => {
    if (!status) return t('common.none');
    const key = STATUS_KEY[status];
    return key ? t(key) : status;
  };

  const statusBadge = (status: string | null) => {
    if (!status) return <Badge variant="muted">{t('tenants.source.direct')}</Badge>;
    const danger = ['rejected', 'revoked', 'locked'];
    const warning = ['pending_review', 'pending_telegram', 'approved_pending_telegram'];
    return <Badge variant={danger.includes(status) ? 'destructive' : warning.includes(status) ? 'warning' : 'success'}>{statusLabel(status)}</Badge>;
  };

  if (loading && rows.length === 0) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  if (error) return <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>;

  return (
    <div>
      <PageHeader title={t('tenants.title')} description={t('tenants.desc', { n: formatNumber(total) })} />

      <SectionCard title={t('tenants.section')} description={t('tenants.sectionDesc')}>
        <div className="mb-3 flex gap-2">
          <Button
            variant={!showDeleted ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setShowDeleted(false); setPage(0); }}
          >
            {t('tenants.tab.active')}
          </Button>
          <Button
            variant={showDeleted ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setShowDeleted(true); setPage(0); }}
          >
            {t('tenants.tab.deleted')}
          </Button>
          <div className="ml-auto">
            <Button size="sm" onClick={() => setShowAddWorkspace(true)}>{t('tenants.addWorkspace')}</Button>
          </div>
        </div>

        <div className="mb-3 flex flex-col gap-3 sm:flex-row">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            placeholder={t('tenants.searchPh')}
            className="flex-1"
          />
          <Button onClick={search} disabled={loading}><Search />{loading ? t('common.searching') : t('common.search')}</Button>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.name')}</TableHead>
              <TableHead>{t('tenants.col.owner')}</TableHead>
              <TableHead>{t('tenants.col.signup')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead>{t('common.plan')}</TableHead>
              <TableHead className="text-right">{t('common.member')}</TableHead>
              <TableHead>{showDeleted ? t('tenants.col.deletedAt') : t('common.created')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((tn) => (
              <TableRow key={tn.id} className="cursor-pointer hover:bg-accent/30" onClick={() => setSelected(tn)}>
                <TableCell>
                  <div className="font-medium">{displayWorkspaceName(tn.name)}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{displayIdentifier(tn.slug, t('common.slug'))}</div>
                  {tn.requestedWorkspaceName && tn.requestedWorkspaceName !== tn.name && (
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {t('tenants.col.requested')} {displayWorkspaceName(tn.requestedWorkspaceName)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="font-medium">{displayEmail(tn.ownerEmail, tn.ownerUserId ?? tn.signupApplicationId) ?? '-'}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {displayPersonName(tn.nickname ?? tn.ownerDisplayName, tn.ownerUserId ?? tn.ownerEmail) ?? '-'}
                    {tn.ownerTelegramUsername && ` · ${displayTelegramHandle(tn.ownerTelegramUsername)}`}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {t('access.queue.field.employeeNumber')}: {displayIdentifier(tn.employeeNumber, t('access.queue.field.employeeNumber')) ?? '-'}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <CompactField label={t('access.queue.field.tgName')} value={displayPersonName(tn.tgName, tn.ownerUserId ?? tn.ownerEmail)} />
                    <CompactField label={t('access.queue.field.referralCode')} value={displayReferralCode(tn.referralCode)} />
                    <CompactField label={t('access.queue.field.telegramChatId')} value={displayIdentifier(tn.ownerTelegramChatId, t('common.telegramChatId'))} />
                    <CompactField label={t('common.detail')} value={displayIdentifier(tn.signupApplicationId, t('access.queue.section'))} />
                  </div>
                </TableCell>
                <TableCell>{statusBadge(tn.signupStatus ?? tn.ownerStatus)}</TableCell>
                <TableCell><Badge variant="muted">{planLabel(t, tn.plan)}</Badge></TableCell>
                <TableCell className="text-right">{formatNumber(Number(tn.memberCount))}</TableCell>
                <TableCell className="text-muted-foreground">
                  {showDeleted ? formatDate(tn.deletedAt) : formatDate(tn.createdAt)}
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  {showDeleted ? (
                    <Button variant="success" size="sm" onClick={() => restoreTenant(tn)}>{t('tenants.action.restoreDeleted')}</Button>
                  ) : (
                    <Button variant="destructive" size="sm" onClick={() => deleteTenant(tn)}>{t('tenants.action.softDelete')}</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">{t('tenants.empty')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
      </SectionCard>

      {/* 新建工作区对话框 */}
      <Dialog open={showAddWorkspace} onOpenChange={(open) => { if (!open) setShowAddWorkspace(false); }}>
        <DialogContent onClose={() => setShowAddWorkspace(false)}>
          <DialogHeader>
            <DialogTitle>{t('tenants.addWorkspace.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium">{t('tenants.addWorkspace.name')}</label>
              <Input
                value={addForm.name}
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t('tenants.addWorkspace.name')}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t('tenants.addWorkspace.slug')}</label>
              <Input
                value={addForm.slug}
                onChange={(e) => setAddForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="acme-corp（留空自动生成）"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddWorkspace(false)}>{t('common.cancel')}</Button>
            <Button onClick={addWorkspace} disabled={addLoading}>
              {addLoading ? t('common.creating') : t('tenants.addWorkspace.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 工作区详情对话框 */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent onClose={() => setSelected(null)}>
          {selected && (
            <div className="space-y-4">
              <div>
                <div className="text-lg font-semibold">{displayWorkspaceName(selected.name)}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  <Badge variant="muted">{planLabel(t, selected.plan)}</Badge>
                  <span className="ml-2">{displayIdentifier(selected.slug, t('common.slug'))}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <DetailRow label={t('common.workspaceId')} value={displayIdentifier(selected.id, t('common.workspaceId'))} />
                <DetailRow label={t('common.slug')} value={displayIdentifier(selected.slug, t('common.slug'))} />
                <DetailRow label={t('tenants.col.requested')} value={displayWorkspaceName(selected.requestedWorkspaceName)} />
                <DetailRow label={t('common.plan')} value={planLabel(t, selected.plan)} />
                <DetailRow label={t('common.created')} value={formatDateTime(selected.createdAt)} />
                {selected.deletedAt && <DetailRow label={t('tenants.col.deletedAt')} value={formatDateTime(selected.deletedAt)} />}
                <DetailRow label={t('common.member')} value={formatNumber(Number(selected.memberCount))} />
                <DetailRow label={t('tenants.col.employees')} value={formatNumber(Number(selected.employeeCount))} />
                <DetailRow label={t('tenants.col.conversations')} value={formatNumber(Number(selected.conversationCount))} />
                <DetailRow label={t('tenants.col.owner')} value={displayEmail(selected.ownerEmail, selected.ownerUserId ?? selected.signupApplicationId)} />
                <DetailRow label={t('access.detail.displayName')} value={displayPersonName(selected.ownerDisplayName, selected.ownerUserId ?? selected.ownerEmail)} />
                <DetailRow label={t('access.detail.employeeNumber')} value={displayIdentifier(selected.employeeNumber, t('access.detail.employeeNumber'))} />
                <DetailRow label={t('access.detail.nickname')} value={displayPersonName(selected.nickname, selected.ownerUserId ?? selected.ownerEmail)} />
                <DetailRow label={t('access.detail.tgName')} value={displayPersonName(selected.tgName, selected.ownerUserId ?? selected.ownerEmail)} />
                <DetailRow label={t('common.telegramAccount')} value={displayTelegramHandle(selected.ownerTelegramUsername)} />
                <DetailRow label={t('common.telegramChatId')} value={displayIdentifier(selected.ownerTelegramChatId, t('common.telegramChatId'))} />
                <DetailRow label={t('access.detail.referralCode')} value={displayReferralCode(selected.referralCode)} />
                <DetailRow label={t('access.queue.section')} value={displayIdentifier(selected.signupApplicationId, t('access.queue.section'))} />
                <DetailRow label={t('common.status')} value={statusLabel(selected.signupStatus ?? selected.ownerStatus)} />
                <DetailRow label={t('common.joined')} value={formatDateTime(selected.reviewedAt)} />
              </div>

              <div className="rounded-md border bg-muted/20 p-3">
                <div className="text-xs font-medium text-muted-foreground mb-1">{t('tenants.col.offline')}</div>
                <div className="flex items-center gap-2">
                  <div className="text-sm flex-1">
                    {t('tenants.minutes', { n: Number(selected.entitlementOfflineGraceMinutes ?? 0) })}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => updateOfflineGrace(selected)}>
                    {t('common.refresh')}
                  </Button>
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground">{t('tenants.offlineDesc')}</div>
              </div>

              <div className="flex justify-end gap-2">
                {selected.deletedAt ? (
                  <Button variant="success" size="sm" onClick={() => restoreTenant(selected)}>{t('tenants.action.restoreDeleted')}</Button>
                ) : (
                  <Button variant="destructive" size="sm" onClick={() => deleteTenant(selected)}>{t('tenants.action.softDelete')}</Button>
                )}
                <Button variant="outline" size="sm" onClick={() => setSelected(null)}>{t('common.close')}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ label, value, mono, small }: { label: string; value: string | null | undefined; mono?: boolean; small?: boolean }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-0.5 break-all ${mono ? 'font-mono' : ''} ${small ? 'text-[11px]' : 'text-sm'}`}>
        {value || <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  );
}

function CompactField({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-0.5 break-all text-[13px] text-foreground ${mono ? 'font-mono' : ''}`}>
        {value || <span className="text-muted-foreground">-</span>}
      </div>
    </div>
  );
}
